import { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  Buttons,
  ButtonsSeparator,
  Page,
  RadioWrapper,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RadioGroup from '../../components/RadioGroup/RadioGroup';
import Table from '../../components/Table/Table';
import { RAW_PROD_TYPE_CODE } from '../../constants/RawProds';
import { useCheckRawProd, useDeleteRawProd, useRawProdSource } from '../../services/rawProd/rawProd.service';
import { RawProdSource } from '../../services/rawProd/rawProd.types';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';
import { StyledButtons } from '../ComboTopping/ComboTopping.styles';
import Modal from '../../components/Modal/Modal';
import { genericErrorShowToastOptions } from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { EditCell } from '../../components/Table/EditCellRenderer/EditCellRenderer.styles';
import { useTheme } from '@emotion/react';
import { EditButton } from './RawProds.style';
import { SubTitle } from '../Classes/ClassForm/ClassForm.styles';

const RawProds = () => {
  const theme = useTheme();

  const gridRef = useRef<AgGridReact>(null);
  const selectedRowsRef = useRef<AgGridReact>(null);
  const [selectedRows, setSelectedRows] = useState<RawProdSource[]>([]);
  const [viewUnused, setViewUnused] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [rawProdsToDelete, setRawProdsToDelete] = useState<any[]>([]);
  const { mutate: deleteRawProd } = useDeleteRawProd();
  const navigate = useNavigate();

  const { data, refetch } = useRawProdSource({ unused: viewUnused }, true);

  const { mutate: checkRawProducts } = useCheckRawProd();

  const memoizedData = useMemo(() => data?.data, [data]);

  const closeModal = () => setModalOpen(false);
  const openModal = () => {
    if (!viewUnused) {
      setModalOpen(true);
      const moaRawProdNo = selectedRows
        .map((row) => row.moaRawProdNo)
        .filter((no): no is number => no !== undefined)
        .map((no) => String(no));
      checkRawProducts(moaRawProdNo, {
        onSuccess: (response) => {
          setRawProdsToDelete(response?.data);
        },
        onError: () => {
          showToast({
            ...genericErrorShowToastOptions,
            label: 'Error checking raw prod',
            id: 'rawProdCheckError',
            toastType: ToastTypes.Error,
          });
        },
      });
    }
    setModalOpen(true);
  };

  const handleOpenConfirmationModal = () => {
    setOpenConfirmationModal(true);
    closeModal();
  }

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
    closeModal();
  };

  const onDeleteRawProduct = () => {
    let rawProdsToProcessDelete;
    setOpenConfirmationModal(false);
    if (viewUnused) {
      rawProdsToProcessDelete = selectedRows;
    } else {
      rawProdsToProcessDelete = rawProdsToDelete
        .filter((rawProd: any) => rawProd.isRawprodUsed !== true)
        .map(rawProd => ({
          ...rawProd,
          moaRawProdNo: Number(rawProd.moaRawProdNo)
        }));
    }
    
    const moaRawProdNo = rawProdsToProcessDelete
      .map((row) => row.moaRawProdNo)
      .filter((no): no is number => no !== undefined)
      .map((no) => String(no));
      
    if (moaRawProdNo.length === 0) {
      showToast({
        label: 'Raw prods in use cannot be deleted',
        id: 'noRawProdToDelete',
        toastType: ToastTypes.Info,
      });
      return;
    }
    
    deleteRawProd(moaRawProdNo, {
      onSuccess: () => {
        showToast({
          label: 'Raw Prod Deleted',
          id: 'rawProdDeleteSuccess',
        });
        refetch();
        setSelectedRows([]);
      },
      onError: () => {
        showToast({
          ...genericErrorShowToastOptions,
          label: 'Error deleting raw prod',
          id: 'rawProdDeleteError',
          toastType: ToastTypes.Error,
        });
      },
    });
  };

  const handleRowSelection = () => {
    setSelectedRows(gridRef.current?.api.getSelectedRows() || []);
  };

  const handleOpenClick = (id: number) => {
    navigate(`/tables/rawproducts/${id}`);
  };

  const handleAddClick = () => {
    navigate(`/tables/rawproducts/add/${selectedRows[0]?.id}`);
  };

  const handleAddNew = () => {
    navigate(`/tables/rawproducts/add`);
  };

  const handleRadioChange = () => {
    setViewUnused(!viewUnused);
    setSelectedRows([]);
  };

  const OpenRawProduct = (props: ICellRendererParams) => {
    return (
      <EditCell onClick={() => handleOpenClick(props.data.id)}>
        <EditButton data-testId={`openRawProdButton-${props.data.id}`} aria-label='edit'>
          <EditOutlinedIcon htmlColor={theme.colors.blue} />
        </EditButton>
      </EditCell>
    );
  };

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Raw Product' />
      <Page>
        <TableWrapper>
          <Table
            rowData={memoizedData}
            ref={gridRef}
            returnedRecords={memoizedData?.length}
            height={'100%'}
            rowSelection={'multiple'}
            onRowSelected={handleRowSelection}
            suppressRowClickSelection={false}
            rowMultiSelectWithClick={true}
            isRowSelectable={(node: any) => (viewUnused ? true : node.data.mxRef !== 'NATION')}
            columnDefs={[
              {
                minWidth: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              {
                minWidth: 70,
                maxWidth: 120,
                field: 'edit',
                headerName: 'Edit',
                flex: 1,
                cellRenderer: OpenRawProduct,
                floatingFilter: false,
                resizable: true,
              },
              { minWidth: 112, field: 'mxRef', headerName: 'MxRef' },
              { minWidth: 112, field: 'moaRawProdNo', headerName: 'MOA Raw Product No.' },
              { minWidth: 112, field: 'rawProdNo', headerName: 'Raw Product No.' },
              { minWidth: 112, field: 'rawProdTpCode', headerName: 'Raw Product Type Code' },
              { minWidth: 112, field: 'description', headerName: 'Description' },
              { minWidth: 112, field: 'shortDescription', headerName: 'Short Description' },
              { minWidth: 112, field: 'statusNo', headerName: 'Status No.' },
              { minWidth: 112, field: 'classNo', headerName: 'Class' },
              { minWidth: 112, field: 'printSeq', headerName: 'Print Seq.' },
              { minWidth: 112, field: 'pcaNo', headerName: 'PCA No.' },
              { minWidth: 112, field: 'occasionNo', headerName: 'Occasion' },
              { minWidth: 112, field: 'invenUomNo', headerName: 'InvenUOM No.' },
              { minWidth: 112, field: 'prodUomNo', headerName: 'ProdUOM No.' },
              { minWidth: 112, field: 'conversionFactor', headerName: 'Conversion Factor' },
              {
                minWidth: 112,
                field: 'buyLocal',
                headerName: 'Buy Local',
                valueFormatter: (params) => (params.value === false ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('buyLocal') === false ? 'N' : 'Y'),
              },
              { minWidth: 112, field: 'accountCode', headerName: 'Account Code' },
              {
                minWidth: 112,
                field: 'usageAlgorithm',
                headerName: 'Usage Algorithm',
              },
              {
                minWidth: 112,
                field: 'frozen',
                headerName: 'Frozen',
                valueFormatter: (params) => (params.value === false ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('frozen') === false ? 'N' : 'Y'),
              },
              { minWidth: 112, field: 'safetyPolicy', headerName: 'Safety Policy' },
              { minWidth: 112, field: 'updatedBy', headerName: 'Update By' },
              {
                minWidth: 112,
                field: 'updatedDate',
                headerName: 'Update Date',
                valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString() : ''),
              },
              { minWidth: 112, field: 'glAccountingCode', headerName: 'GL Account Code' },
              { minWidth: 112, field: 'glAccountCategoryDescription', headerName: 'GL Account Category' },
            ]}
            footerButtons={
              selectedRows?.length === 1 &&
              selectedRows[0]?.mxRef !== undefined &&
              selectedRows[0]?.rawProdTpCode !== RAW_PROD_TYPE_CODE.SubAssembly
                ? [
                    {
                      label: selectedRows[0]?.mxRef === 'NATION' ? 'Add Like Nation' : 'Add Override',
                      onClick: handleAddClick,
                      testId: selectedRows[0]?.mxRef === 'NATION' ? 'addLikeNationButton' : 'addOverrideButton',
                      variant: Variant.Secondary,
                    },
                  ]
                : []
            }
          />
          {!viewUnused && <SubTitle> *Rawprod NATION can't be deleted.</SubTitle>}
        </TableWrapper>
        <Buttons>
          <Button label='Add New' variant={Variant.Secondary} onClick={handleAddNew} />
          <Button
            label='Delete'
            variant={Variant.Secondary}
            onClick={openModal}
            disabled={selectedRows.length === 0}
            testId={'deleteRawProdButton'}
          />
          <ButtonsSeparator />
          <RadioWrapper>
            <RadioGroup
              options={[
                {
                  id: 'all',
                  label: 'All',
                },
                {
                  id: 'unused',
                  label: 'Unused',
                },
              ]}
              selected={viewUnused ? 'unused' : 'all'}
              onChange={handleRadioChange}
              id='menu-option'
              label='View Options'
            />
          </RadioWrapper>
        </Buttons>
        <Modal id='removeRawProdModal' open={modalOpen} onClose={closeModal} title='Remove Raw Prod'>
          <div>
            <>
              <Table
                height='400px'
                rowData={viewUnused ? selectedRows : rawProdsToDelete}
                ref={selectedRowsRef}
                suppressRowClickSelection={false}
                rowMultiSelectWithClick={true}
                returnedRecords={viewUnused ? selectedRows.length : rawProdsToDelete?.length}
                isRowSelectable={(node: any) => node.data.isRawprodUsed !== true}
                getRowStyle={(params) => {
                  if (params.data.isRawprodUsed) {
                    return { backgroundColor: theme.colors.lightRed };
                  }
                  return undefined;
                }}
                rowSelection='multiple'
                columnDefs={[
                  { minWidth: 112, field: viewUnused ? 'mxRef' : 'mxref', headerName: 'MxRef' },
                  { minWidth: 112, field: 'moaRawProdNo', headerName: 'MOA Raw Prod No.' },
                  { minWidth: 112, field: 'description', headerName: 'Description' },
                  {
                    minWidth: 112,
                    field: 'isRawprodUsed',
                    headerName: viewUnused ? '' : 'Is RawProd Used',
                    floatingFilter: viewUnused ? false : true,
                    valueFormatter: (params) => (viewUnused ? '' : params.value === false ? 'No' : 'Yes'),
                  },
                ]}
              />
              <StyledButtons>
                <Button label='Cancel' testId='closeModalButton' onClick={closeModal} variant={Variant.Secondary} />
                <Button
                  label='Delete'
                  testId='deleteRawProdModalButton'
                  onClick={handleOpenConfirmationModal}
                  variant={Variant.Primary}
                />
              </StyledButtons>
            </>
          </div>
        </Modal>

        <Modal id="deleteRawprodModal" title='We will delete just the Raw Prods that are not in use.' open={openConfirmationModal} onClose={handleCloseConfirmationModal}>
          <div>
            <StyledButtons>
              <Button label='Cancel' testId='closeModalButton' onClick={handleCloseConfirmationModal} variant={Variant.Secondary} />
              <Button
                label='Delete'
                testId='deleteRawProdModalButton'
                onClick={onDeleteRawProduct}
                variant={Variant.Primary}
              />
            </StyledButtons>
            </div>

        </Modal>
      </Page>
    </>
  );
};

export default RawProds;
